import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import type { Runtime } from './lib/runtime.ts'
import type { AppRouter } from './routes.tsx'
import { SessionProvider } from './session/context.tsx'

/**
 * The application: the query client, the session, and the router.
 *
 * **It creates none of them.** They are made once by `createRuntime()` at module scope
 * and handed in — the query client in particular, because rebuilding one inside a
 * component is **CF1** (§5.3), the bug that wiped the old app's cache on every theme
 * change. A component that cannot construct a client cannot rebuild one.
 */
export type AppProps = {
  readonly runtime: Runtime
  readonly router: AppRouter
}

export function App({ runtime, router }: AppProps) {
  return (
    <QueryClientProvider client={runtime.queryClient}>
      <SessionProvider store={runtime.session}>
        {/*
          **The router is mounted once and stays mounted, at every width.** Nothing here or in
          the route tree swaps it for another page. A width gate that did — below 1024 px, until
          P08 retired it — unmounted the router and with it its only subscription to the browser
          history: a Back press then moved the URL and not the router, and on widening the router
          came back on the page it last showed (`fix-console-narrow-history-tenant-carryover`,
          QA D4). Keeping the router mounted is what keeps that from coming back.
        */}
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  )
}
